import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useMemo } from 'react';
import { useT, hasValue } from '@progress-fe/core';
import { Box, Flex, Grid, Button, GridItem, Input } from '@chakra-ui/react';
import { InputNumber, Select } from '../../inputs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldName, JsFieldError, JsFieldInspector } from '../../jsFormBase';
/**
 * Component renders "/schemas/jsf-flow-composition-lump-components" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TFormData.
 */
const FlowCompositionLumpComponentsJsFieldFC = (props) => {
    const context = useContext(JsFormConfigContext) || {};
    const { components, runCallback, isInspector } = context;
    const { lumpList = [] } = components || {};
    const { t } = useT();
    const formData = useMemo(() => (hasValue(props.formData) ? props.formData : null), [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig || null,
        values: formData?.fieldValues || null,
        onChange: props.onChange,
        watchPropsChanges: true
    });
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const getComponentNameByUuid = (uuid) => {
        return lumpList.find((i) => i.uuid === uuid)?.name ?? '';
    };
    const selectedComponentsIds = useMemo(() => {
        return fieldValues ? Object.keys(fieldValues.composition || {}) : [];
    }, [fieldValues]);
    const isFieldInvalid = !!props.rawErrors;
    const onChangeUomValue = (option) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, uomValue: option.value });
        }
    };
    const onChangeCompositionValue = (uuid, value) => {
        if (fieldValues) {
            const compositionCopy = structuredClone(fieldValues.composition);
            compositionCopy[uuid] = value;
            onChangeFieldValues({ ...fieldValues, composition: compositionCopy });
        }
    };
    const handleCalculateCallback = async () => {
        const callbackCode = fieldConfig?.normalization.callbackCode;
        if (!callbackCode || !runCallback) {
            return;
        }
        const formDataResult = await runCallback(callbackCode);
        if (hasValue(formDataResult)) {
            const result = formDataResult;
            onChangeFieldConfig(result.fieldConfig);
            onChangeFieldValues(result.fieldValues);
        }
    };
    if (!fieldConfig) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    const isReadOnly = props.schema.readOnly;
    const hasComponents = !!selectedComponentsIds.length;
    const selectedUom = fieldConfig?.uom.options.find((opt) => opt.value === fieldValues?.uomValue);
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsx(Box, { "data-testid": "FlowCompositionLumpComponentsJsField-test", "data-name": props.name, children: _jsxs(Flex, { p: "4px 0", flexDirection: "column", gap: "8px", children: [_jsxs(Grid, { gridTemplateColumns: `minmax(0, 184px) 1fr`, alignItems: 'center', children: [_jsx(JsFieldName, { name: t('jsfields.FlowComposition.uom') }), _jsx(Select, { name: props.name, value: selectedUom, options: fieldConfig.uom.options, placeholder: t('common.optionNotSelected'), isDisabled: isReadOnly || fieldConfig.uom.readOnly, onChange: (newValue) => onChangeUomValue(newValue) })] }), _jsxs(GridItem, { p: "4px", borderRadius: "4px", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', children: [_jsxs(Grid, { gap: "4px", p: "0 0 4px 0", alignItems: "center", borderBottom: "1px solid", borderColor: "border", templateColumns: "176px 1fr", children: [_jsx(GridItem, { children: t('fields.lumpComponent') }), _jsx(GridItem, { children: t('fields.amount') })] }), _jsx(Box, { p: "2px 0", children: selectedComponentsIds.map((uuid) => {
                                    return (_jsxs(Grid, { gap: "4px", p: "2px 0", templateColumns: "176px 1fr", children: [_jsx(Flex, { align: "center", className: 'cut-text', children: getComponentNameByUuid(uuid) }), _jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", sx: { width: '100%' }, disabled: isReadOnly, rightElement: selectedUom?.unit, onChange: (v) => onChangeCompositionValue(uuid, v), value: fieldValues?.composition[uuid] || undefined })] }, uuid));
                                }) }), _jsxs(Grid, { gap: "4px", p: "4px 0 0 0", borderTop: hasComponents ? '1px solid' : 'none', borderColor: "border", templateColumns: fieldValues?.totalValue ? '176px 1fr auto' : '1fr', children: [fieldValues?.totalValue && (_jsxs(_Fragment, { children: [_jsx(Flex, { align: "center", children: t('common.total') }), _jsx(Input, { size: "xs", isDisabled: true, variant: "outline", value: fieldValues?.totalValue })] })), !isReadOnly && fieldConfig.normalization?.possible && (_jsx(Button, { size: "xs", variant: "ghost", onClick: handleCalculateCallback, children: t('actions.calculate') }))] })] })] }) }) }));
};
export const FlowCompositionLumpComponentsJsField = FlowCompositionLumpComponentsJsFieldFC;
//# sourceMappingURL=FlowCompositionLumpComponentsJsField.js.map